import { Link, Spacer, Typography, useSnackbar } from "@fullscript/aviary-web";
import { Trans, useTranslation } from "react-i18next";

import { l } from "@shared/locales/i18n";
import {
  useResendEditMfaPasscodeSharedMutation as useResendEditMFAPasscode,
  type ResendEditMfaPasscodeSharedMutation as ResendEditMFAPasscodeData,
} from "@shared/MultifactorAuthentication/data/ResendEditMFAPasscode.mutation.generated";
import {
  useResendSetupMfaPasscodeSharedMutation as useResendMFAPasscode,
  type ResendSetupMfaPasscodeSharedMutation as ResendMFAPasscodeData,
} from "@shared/MultifactorAuthentication/data/ResendSetupMFAPasscode.mutation.generated";
import { useLocation } from "@shared/react-router-dom/react-router-dom";

import * as styles from "./ResendOtp.styles";

interface Props {
  phoneLast4?: string;
  email?: string;
  isVerifyChangeFlow?: boolean;
}

interface HistoryState {
  mfaToken?: string;
}

const ResendOtp = ({ phoneLast4, isVerifyChangeFlow, email }: Props) => {
  const { state }: { state: HistoryState } = useLocation();

  const { t } = useTranslation();
  const { criticalSnackbar, successSnackbar } = useSnackbar();

  const otpMethodName = phoneLast4 ? "text message" : "email";

  const [resendPasscode, { loading: loadingResendPasscode }] = useResendMFAPasscode({
    variables: {
      input: {
        token: state?.mfaToken,
      },
    },
    onCompleted: (data: ResendMFAPasscodeData) => {
      const hasErrors = data?.auth?.userResendEmailSetupTwoFactorAuthentication?.errors?.message;
      const isSent = data?.auth?.userResendEmailSetupTwoFactorAuthentication?.sent;

      if (hasErrors || !isSent) {
        return criticalSnackbar(t(l.mfa.resendOtp.otpNotSent, { otpMethodName }));
      }

      if (isSent) {
        if (otpMethodName === "email") {
          successSnackbar(t(l.mfa.resendOtp.emailSent, { email }));
        } else if (otpMethodName === "text message") {
          successSnackbar(t(l.mfa.resendOtp.smsSent, { phoneLast4 }));
        }
      }
    },
    onError: ({ message }) => criticalSnackbar(message),
  });

  const [resendEditPasscode, { loading: loadingResendEditPasscode }] = useResendEditMFAPasscode({
    variables: {
      input: {},
    },
    onCompleted: (data: ResendEditMFAPasscodeData) => {
      const hasErrors = data?.auth?.userResendEditTwoFactorAuthentication?.errors?.message;
      const isSent = data?.auth?.userResendEditTwoFactorAuthentication?.sent;

      if (hasErrors || !isSent) {
        return criticalSnackbar(t(l.mfa.resendOtp.otpNotSent, { otpMethodName }));
      }

      if (isSent) {
        if (otpMethodName === "email") {
          successSnackbar(t(l.mfa.resendOtp.emailSent, { email }));
        } else if (otpMethodName === "text message") {
          successSnackbar(t(l.mfa.resendOtp.smsSent, { phoneLast4 }));
        }
      }
    },
    onError: ({ message }) => criticalSnackbar(message),
  });

  const handleResendPasscode = () => {
    if (loadingResendPasscode || loadingResendEditPasscode) return;

    if (isVerifyChangeFlow) {
      resendEditPasscode();
    } else {
      resendPasscode();
    }
  };

  return (
    <div css={styles.wrapper}>
      <Typography variant="footnote" isMarginless intention="neutral" colorIntensity="subtle">
        <Trans
          i18nKey={
            otpMethodName === "email" ? l.mfa.resendOtp.didntGetEmail : l.mfa.resendOtp.didntGetSMS
          }
          components={{ resendLink: <Link onClick={handleResendPasscode} /> }}
        />
      </Typography>
      <Spacer size="small" />
      <Typography
        variant="footnote"
        customCss={styles.expirationMessage}
        isMarginless
        intention="neutral"
        colorIntensity="subtle"
      >
        {t(l.mfa.resendOtp.codeWillExpireIn10)}
      </Typography>
    </div>
  );
};

export { ResendOtp };
